import { useState, useEffect, useContext, useMemo } from "react";
import { getMeals } from "../api/meals";
import { getPlanForDates } from "../api/plans";
import { getGroceryData, saveGroceryData } from "../api/grocery";
import { AuthContext } from "../context/AuthContext";

const SECTIONS = [
  "Produce",
  "Dairy & Eggs",
  "Meat & Seafood",
  "Bakery",
  "Pantry",
  "Frozen",
  "Beverages",
  "Other"
];

const MEAL_TYPES = ["breakfast","lunch","dinner","snacks"];

function todayStr(){
  const d = new Date();
  return [
    d.getFullYear(),
    String(d.getMonth()+1).padStart(2,"0"),
    String(d.getDate()).padStart(2,"0")
  ].join("-");
}

function offsetDateStr(offsetDays){
  const d = new Date();
  d.setDate(d.getDate() + offsetDays);
  return [
    d.getFullYear(),
    String(d.getMonth()+1).padStart(2,"0"),
    String(d.getDate()).padStart(2,"0")
  ].join("-");
}

function getDatesInRange(startStr, endStr){
  const dates = [];
  const cur = new Date(startStr + "T00:00:00");
  const end = new Date(endStr   + "T00:00:00");
  while(cur <= end){
    dates.push([
      cur.getFullYear(),
      String(cur.getMonth()+1).padStart(2,"0"),
      String(cur.getDate()).padStart(2,"0")
    ].join("-"));
    cur.setDate(cur.getDate()+1);
  }
  return dates;
}

function combineIngredients(ingredientList){
  const map = new Map();
  for(const ing of ingredientList){
    const rawName = (ing.name || "").trim();
    if(!rawName) continue;
    const key = `${rawName.toLowerCase()}||${(ing.unit||"").trim().toLowerCase()}`;
    if(map.has(key)){
      const existing = map.get(key);
      const a = parseFloat(existing.quantity);
      const b = parseFloat(ing.quantity);
      if(!isNaN(a) && !isNaN(b)) existing.quantity = String(a + b);
    } else {
      map.set(key, {
        name:    rawName,
        quantity: ing.quantity || "",
        unit:    (ing.unit || "").trim(),
        section: ing.section || "Other",
        key
      });
    }
  }
  return Array.from(map.values());
}

function formatQty(qty){
  const n = parseFloat(qty);
  if(isNaN(n)) return qty;
  return n % 1 === 0 ? String(Math.round(n)) : String(Math.round(n * 100) / 100);
}

export default function GroceryPage({ meals, setMeals }){

  const { householdId } = useContext(AuthContext);

  const [startDate, setStartDate] = useState(
    ()=>localStorage.getItem("groceryStart") || todayStr()
  );
  const [endDate, setEndDate] = useState(
    ()=>localStorage.getItem("groceryEnd") || offsetDateStr(6)
  );
  const [plan, setPlan] = useState({});
  const [checked, setChecked] = useState(()=>{
    try{ return JSON.parse(localStorage.getItem("groceryChecked") || "{}"); }
    catch{ return {}; }
  });
  const [customItems, setCustomItems] = useState(()=>{
    try{ return JSON.parse(localStorage.getItem("groceryCustomItems") || "[]"); }
    catch{ return []; }
  });
  const [dataLoaded, setDataLoaded] = useState(false);
  const [addName, setAddName] = useState("");
  const [addSection, setAddSection] = useState("Other");

  // Persist date range
  useEffect(()=>{
    localStorage.setItem("groceryStart", startDate);
    localStorage.setItem("groceryEnd",   endDate);
  },[startDate, endDate]);

  // Persist checked state to localStorage
  useEffect(()=>{
    localStorage.setItem("groceryChecked", JSON.stringify(checked));
  },[checked]);

  // Load checked + custom items from Firestore on mount (syncs across devices)
  useEffect(()=>{
    if(!householdId) return;
    getGroceryData(householdId).then(data=>{
      if(data !== null){
        // Firestore has data — use it as authoritative source
        setChecked(data.checked);
        setCustomItems(data.customItems);
      }
      // If data is null (no Firestore doc yet), keep localStorage values —
      // the save effect below will seed Firestore with them
      setDataLoaded(true);
    });
  },[householdId]);

  // Save checked + custom items to Firestore (debounced)
  // dataLoaded in deps means this fires once load completes, seeding Firestore if empty
  useEffect(()=>{
    if(!householdId || !dataLoaded) return;
    const timer = setTimeout(()=>{
      saveGroceryData(householdId, checked, customItems);
    }, 1500);
    return ()=>clearTimeout(timer);
  },[householdId, dataLoaded, checked, customItems]);

  // Persist custom items
  useEffect(()=>{
    localStorage.setItem("groceryCustomItems", JSON.stringify(customItems));
  },[customItems]);

  // Load meals if needed
  useEffect(()=>{
    if(!householdId || meals.length > 0) return;
    getMeals(householdId).then(setMeals);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[householdId]);

  // Fetch plan whenever date range changes
  useEffect(()=>{
    if(!householdId || !startDate || !endDate || startDate > endDate) return;
    const dates = getDatesInRange(startDate, endDate);
    getPlanForDates(householdId, dates).then(setPlan);
  },[householdId, startDate, endDate]);

  const mealMap = useMemo(()=>
    Object.fromEntries(meals.map(m=>[m.id, m]))
  ,[meals]);

  // Build combined grocery list from plan
  const mealItems = useMemo(()=>{
    if(!startDate || !endDate || startDate > endDate) return [];
    const dates = getDatesInRange(startDate, endDate);
    const allIngredients = dates.flatMap(dateStr=>{
      const dayPlan = plan[dateStr] || {};
      return MEAL_TYPES.flatMap(type=>
        (dayPlan[type] || []).flatMap(mealId=>{
          const meal = mealMap[mealId];
          return meal ? (meal.ingredients || []) : [];
        })
      );
    });
    return combineIngredients(allIngredients);
  },[plan, mealMap, startDate, endDate]);

  // All items = meal-generated + custom
  const allItems = useMemo(()=>[...mealItems, ...customItems],[mealItems, customItems]);

  // Unchecked items grouped by section
  const uncheckedBySection = useMemo(()=>{
    const groups = {};
    for(const section of SECTIONS) groups[section] = [];
    for(const item of allItems){
      if(checked[item.key]) continue;
      const s = SECTIONS.includes(item.section) ? item.section : "Other";
      groups[s].push(item);
    }
    return groups;
  },[allItems, checked]);

  // Checked items flat list
  const checkedItems = useMemo(()=>
    allItems.filter(i=>checked[i.key])
  ,[allItems, checked]);

  function toggleChecked(key){
    setChecked(prev=>({ ...prev, [key]: !prev[key] }));
  }

  function clearChecked(){
    setChecked({});
  }

  function addCustomItem(){
    const name = addName.trim();
    if(!name) return;
    const key = `custom_${Date.now()}_${Math.random().toString(36).slice(2)}`;
    setCustomItems(prev=>[...prev, { name, section: addSection, quantity: "", unit: "", key, isCustom: true }]);
    setAddName("");
  }

  function removeCustomItem(key){
    setCustomItems(prev=>prev.filter(i=>i.key !== key));
    setChecked(prev=>{ const n={...prev}; delete n[key]; return n; });
  }

  const totalItems   = allItems.length;
  const checkedCount = checkedItems.length;
  const hasItems     = totalItems > 0;
  const hasUnchecked = allItems.some(i=>!checked[i.key]);

  function renderItem(item){
    const isChecked = !!checked[item.key];
    const qtyUnit = [
      item.quantity ? formatQty(item.quantity) : "",
      item.unit
    ].filter(Boolean).join(" ");
    return(
      <label
        key={item.key}
        className={`groceryItem${isChecked ? " groceryItemChecked" : ""}`}
      >
        <input
          type="checkbox"
          checked={isChecked}
          onChange={()=>toggleChecked(item.key)}
        />
        <span className="groceryItemText">
          {qtyUnit && (
            <span className="groceryItemQty">{qtyUnit}</span>
          )}
          {item.name}
        </span>
        {item.isCustom && (
          <button
            className="groceryRemoveBtn"
            onClick={e=>{ e.preventDefault(); removeCustomItem(item.key); }}
            title="Remove"
          >×</button>
        )}
      </label>
    );
  }

  return(

    <div>

      <div className="pageHeader">
        <h2>Grocery List</h2>
        {hasItems && checkedCount > 0 && (
          <button className="discardBtn" onClick={clearChecked}>
            Clear checked
          </button>
        )}
      </div>

      {/* DATE RANGE */}
      <div className="groceryDateRange">
        <label>From</label>
        <input
          type="date"
          value={startDate}
          onChange={e=>setStartDate(e.target.value)}
        />
        <label>To</label>
        <input
          type="date"
          value={endDate}
          onChange={e=>setEndDate(e.target.value)}
        />
      </div>

      {/* ADD CUSTOM ITEM */}
      <div className="groceryAddRow">
        <input
          className="groceryAddInput"
          type="text"
          placeholder="Add an item..."
          value={addName}
          onChange={e=>setAddName(e.target.value)}
          onKeyDown={e=>{ if(e.key==="Enter") addCustomItem(); }}
        />
        <select
          className="groceryAddSelect"
          value={addSection}
          onChange={e=>setAddSection(e.target.value)}
        >
          {SECTIONS.map(s=>(
            <option key={s} value={s}>{s}</option>
          ))}
        </select>
        <button className="groceryAddBtn" onClick={addCustomItem}>Add</button>
      </div>

      {/* PROGRESS */}
      {hasItems && (
        <p className="groceryProgress">
          {checkedCount} / {totalItems} items checked
          <span className="groceryProgressBar">
            <span
              className="groceryProgressFill"
              style={{width:`${(checkedCount/totalItems)*100}%`}}
            />
          </span>
        </p>
      )}

      {/* EMPTY STATE */}
      {!hasItems && startDate <= endDate && (
        <div className="card textMuted" style={{marginTop:16}}>
          No meals planned for this period. Add meals to your{" "}
          <a href="/planner" className="linkOlive">planner</a> first, or add items above.
        </div>
      )}

      {/* UNCHECKED — grouped by section */}
      {hasUnchecked && SECTIONS.map(section=>{
        const items = uncheckedBySection[section];
        if(!items || items.length === 0) return null;
        return(
          <div key={section} className="grocerySection">
            <h3 className="grocerySectionTitle">{section}</h3>
            {items.map(item=>renderItem(item))}
          </div>
        );
      })}

      {/* CHECKED — flat list at bottom */}
      {checkedItems.length > 0 && (
        <div className="grocerySection grocerySectionChecked">
          <h3 className="grocerySectionTitle">In Cart ({checkedItems.length})</h3>
          {checkedItems.map(item=>renderItem(item))}
        </div>
      )}

    </div>

  );

}
